import React, { useMemo } from 'react';
import { Box, BoxProps } from '@mui/material';
import classNames from 'classnames';

/**
 * @see https://gitlab.com/mindtickle/dsr/dsr-client/-/blob/track/integration/src/lib/Scroll.ts
 */
const LAYOUT_CONTENT_CLASS_NAME = 'AppLayout__content';

export interface LayoutContentProps {
  background?: string;
  className?: string;
  disableOverflow?: boolean;
  disablePadding?: boolean;
  disableScroll?: boolean;
  sx?: BoxProps['sx'];
}

type Props = React.PropsWithChildren<LayoutContentProps>;

const LayoutContent = React.forwardRef<HTMLDivElement, Props>(
  (props: Props, ref): JSX.Element => {
    const {
      background,
      children,
      className,
      disableOverflow = false,
      disablePadding = false,
      disableScroll = false,
      sx,
    } = props;

    const styles = useMemo((): BoxProps['sx'] => {
      const styles: BoxProps['sx'] = {
        background,
        boxSizing: 'border-box',
        flex: '1',
        height: 'calc(100vh - 64px)',
        maxHeight: '100vh',
        overflow: disableScroll ? 'hidden' : undefined,
        padding: disablePadding ? 0 : '24px 0',
        position: 'relative',
        transition: 'background 300ms ease',
        ...sx,
      };

      if (disableOverflow) {
        return styles;
      }

      return {
        ...styles,
        overflowX: 'hidden',
        overflowY: 'auto',
        /**
         * Scrollbars glitches: we got several reports of scrollbars flickering due
         * to a combination of SizeMe calculations in very specific resolutions
         *
         * The `scrollbar-gutter` css property helps mitigating this issue by
         * removing the scrollbars from the width calculation.
         *
         * @see https://developer.mozilla.org/en-US/docs/Web/CSS/scrollbar-gutter
         * @see https://caniuse.com/?search=scrollbar-gutter
         */
        scrollbarGutter: 'stable',
      };
    }, [background, disableOverflow, disablePadding, disableScroll, sx]);

    return (
      <Box
        className={classNames(className, LAYOUT_CONTENT_CLASS_NAME)}
        ref={ref}
        sx={styles}
      >
        {children}
      </Box>
    );
  }
);

export default LayoutContent;
